import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, FileText, CreditCard, Tag, Plus, Edit3, Trash2, Save, X,
  AlertTriangle, ChevronDown, ChevronUp, Palette, GripVertical
} from 'lucide-react';
import { shiftTypes, noteTemplates, paymentMethods, billingCategories } from '../../data/mockData';

const KEYS = {
  shifts: 'medfast_config_shifts',
  notes: 'medfast_config_note_templates',
  payments: 'medfast_config_payment_methods',
  billing: 'medfast_config_billing_categories'
};

const COLORS = [
  'bg-blue-100', 'bg-green-100', 'bg-purple-100', 'bg-yellow-100',
  'bg-red-100', 'bg-pink-100', 'bg-indigo-100', 'bg-teal-100', 'bg-orange-100', 'bg-cyan-100'
];

const load = (key, fallback) => {
  try {
    const d = JSON.parse(localStorage.getItem(key));
    return d && d.length > 0 ? d : fallback;
  } catch { return fallback; }
};

const persist = (key, data) => localStorage.setItem(key, JSON.stringify(data));

const seedNoteTemplates = () => Object.entries(noteTemplates).map(([slug, val]) => ({
  id: slug, title: val.title, fields: val.fields
}));

const seedBillingCategories = () => billingCategories.map((c, i) => ({
  id: `cat_${i}`, name: c.name, amount: c.amount, percentage: c.percentage
}));

const ConfigManagement = () => {
  const [activeSection, setActiveSection] = useState('shifts');
  const [shifts, setShifts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [billing, setBilling] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadAll = useCallback(() => {
    setShifts(load(KEYS.shifts, [...shiftTypes]));
    setNotes(load(KEYS.notes, seedNoteTemplates()));
    setPayments(load(KEYS.payments, [...paymentMethods]));
    setBilling(load(KEYS.billing, seedBillingCategories()));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const getData = () => {
    if (activeSection === 'shifts') return shifts;
    if (activeSection === 'notes') return notes;
    if (activeSection === 'payments') return payments;
    return billing;
  };

  const setData = (data) => {
    if (activeSection === 'shifts') { persist(KEYS.shifts, data); setShifts(data); }
    else if (activeSection === 'notes') { persist(KEYS.notes, data); setNotes(data); }
    else if (activeSection === 'payments') { persist(KEYS.payments, data); setPayments(data); }
    else { persist(KEYS.billing, data); setBilling(data); }
  };

  const openCreate = () => {
    setEditingItem(null);
    if (activeSection === 'shifts') setFormData({ id: '', name: '', startTime: '07:00', endTime: '15:00', color: 'bg-blue-100' });
    else if (activeSection === 'notes') setFormData({ id: '', title: '', fields: [] });
    else if (activeSection === 'payments') setFormData({ id: '', name: '', percentage: 0 });
    else setFormData({ id: '', name: '', amount: 0, percentage: 0 });
    setShowForm(true);
    setError(null);
  };

  const openEdit = (item) => {
    setEditingItem(item.id);
    setFormData({ ...item, fields: item.fields ? [...item.fields] : undefined });
    setShowForm(true);
    setError(null);
  };

  const handleSave = () => {
    const nameField = activeSection === 'notes' ? 'title' : 'name';
    if (!formData[nameField]?.trim()) {
      setError(`${activeSection === 'notes' ? 'Title' : 'Name'} is required`);
      return;
    }

    const data = getData();
    if (editingItem) {
      setData(data.map(d => d.id === editingItem ? { ...d, ...formData } : d));
    } else {
      const id = formData.id || formData[nameField].toLowerCase().replace(/\s+/g, '_');
      if (data.find(d => d.id === id)) {
        setError('An item with this ID already exists');
        return;
      }
      setData([...data, { ...formData, id }]);
    }
    setShowForm(false);
    setError(null);
  };

  const handleDelete = (id) => {
    setData(getData().filter(d => d.id !== id));
    setDeleteConfirm(null);
  };

  const handleReset = () => {
    if (activeSection === 'shifts') { persist(KEYS.shifts, [...shiftTypes]); setShifts([...shiftTypes]); }
    else if (activeSection === 'notes') { const d = seedNoteTemplates(); persist(KEYS.notes, d); setNotes(d); }
    else if (activeSection === 'payments') { persist(KEYS.payments, [...paymentMethods]); setPayments([...paymentMethods]); }
    else { const d = seedBillingCategories(); persist(KEYS.billing, d); setBilling(d); }
  };

  const [newField, setNewField] = useState('');

  const sections = [
    { id: 'shifts', label: 'Shift Types', icon: Clock, count: shifts.length, color: 'blue' },
    { id: 'notes', label: 'Note Templates', icon: FileText, count: notes.length, color: 'green' },
    { id: 'payments', label: 'Payment Methods', icon: CreditCard, count: payments.length, color: 'purple' },
    { id: 'billing', label: 'Billing Categories', icon: Tag, count: billing.length, color: 'orange' }
  ];

  const renderShiftForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Shift Name *</label>
          <input type="text" value={formData.name || ''} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Morning" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button key={c} onClick={() => setFormData(p => ({ ...p, color: c }))}
                className={`w-8 h-8 rounded-lg ${c} border-2 ${formData.color === c ? 'border-gray-800 ring-2 ring-offset-1 ring-blue-400' : 'border-transparent'}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
          <input type="time" value={formData.startTime || ''} onChange={e => setFormData(p => ({ ...p, startTime: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
          <input type="time" value={formData.endTime || ''} onChange={e => setFormData(p => ({ ...p, endTime: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>
  );

  const renderNoteForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Template Title *</label>
        <input type="text" value={formData.title || ''} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
          placeholder="e.g. Progress Note" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fields</label>
        <div className="space-y-2">
          {(formData.fields || []).map((field, i) => (
            <div key={i} className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-gray-400" />
              <span className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm">{field.replace(/_/g, ' ')}</span>
              <button onClick={() => setFormData(p => ({ ...p, fields: p.fields.filter((_, idx) => idx !== i) }))}
                className="p-1 hover:bg-red-100 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input type="text" value={newField} onChange={e => setNewField(e.target.value)}
              placeholder="Add field name..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
              onKeyDown={e => {
                if (e.key === 'Enter' && newField.trim()) {
                  setFormData(p => ({ ...p, fields: [...(p.fields || []), newField.trim().toLowerCase().replace(/\s+/g, '_')] }));
                  setNewField('');
                }
              }} />
            <button onClick={() => {
              if (newField.trim()) {
                setFormData(p => ({ ...p, fields: [...(p.fields || []), newField.trim().toLowerCase().replace(/\s+/g, '_')] }));
                setNewField('');
              }
            }} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm">Add</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method Name *</label>
        <input type="text" value={formData.name || ''} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Direct Debit" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Usage Percentage (%)</label>
        <input type="number" min="0" max="100" value={formData.percentage || 0}
          onChange={e => setFormData(p => ({ ...p, percentage: parseInt(e.target.value) || 0 }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
      </div>
    </div>
  );

  const renderBillingForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
        <input type="text" value={formData.name || ''} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Accommodation" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Amount ($)</label>
          <input type="number" step="0.01" value={formData.amount || 0}
            onChange={e => setFormData(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Budget Percentage (%)</label>
          <input type="number" min="0" max="100" value={formData.percentage || 0}
            onChange={e => setFormData(p => ({ ...p, percentage: parseInt(e.target.value) || 0 }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" />
        </div>
      </div>
    </div>
  );

  const getForm = () => {
    if (activeSection === 'shifts') return renderShiftForm();
    if (activeSection === 'notes') return renderNoteForm();
    if (activeSection === 'payments') return renderPaymentForm();
    return renderBillingForm();
  };

  const renderItem = (item) => {
    if (activeSection === 'shifts') {
      return (
        <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg ${item.color || 'bg-gray-100'} flex items-center justify-center`}>
              <Clock className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">{item.name}</h4>
              <p className="text-sm text-gray-500">{item.startTime} — {item.endTime}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-blue-100 rounded-lg"><Edit3 className="w-4 h-4 text-blue-600" /></button>
            <button onClick={() => setDeleteConfirm(item.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
        </div>
      );
    }
    if (activeSection === 'notes') {
      return (
        <div key={item.id} className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><FileText className="w-5 h-5 text-green-600" /></div>
              <div>
                <h4 className="font-semibold text-gray-800">{item.title}</h4>
                <p className="text-xs text-gray-500">{item.id}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-green-100 rounded-lg"><Edit3 className="w-4 h-4 text-green-600" /></button>
              <button onClick={() => setDeleteConfirm(item.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(item.fields || []).map((f, i) => (
              <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">{f.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      );
    }
    if (activeSection === 'payments') {
      return (
        <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-purple-100 rounded-lg"><CreditCard className="w-5 h-5 text-purple-600" /></div>
            <div>
              <h4 className="font-semibold text-gray-800">{item.name}</h4>
              <p className="text-sm text-gray-500">{item.percentage}% usage</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${Math.min(item.percentage, 100)}%` }} />
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-purple-100 rounded-lg"><Edit3 className="w-4 h-4 text-purple-600" /></button>
              <button onClick={() => setDeleteConfirm(item.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={item.id || item.name} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-orange-100 rounded-lg"><Tag className="w-5 h-5 text-orange-600" /></div>
          <div>
            <h4 className="font-semibold text-gray-800">{item.name}</h4>
            <p className="text-sm text-gray-500">${(item.amount || 0).toLocaleString()} — {item.percentage}%</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-orange-100 rounded-lg"><Edit3 className="w-4 h-4 text-orange-600" /></button>
          <button onClick={() => setDeleteConfirm(item.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
        </div>
      </div>
    );
  };

  const accentColor = sections.find(s => s.id === activeSection)?.color || 'blue';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Configuration Management</h2>
          <p className="text-gray-600 text-sm">Manage shift types, note templates, payment methods, and billing categories</p>
        </div>
      </div>

      {/* Section Nav */}
      <div className="flex gap-3 flex-wrap">
        {sections.map(s => {
          const Icon = s.icon;
          const active = activeSection === s.id;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                active ? `bg-${s.color}-100 text-${s.color}-800 ring-2 ring-${s.color}-300` : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>
              <Icon className="w-4 h-4" />
              {s.label}
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${active ? `bg-${s.color}-200` : 'bg-gray-100'}`}>{s.count}</span>
            </button>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{getData().length} items configured</p>
        <div className="flex gap-2">
          <button onClick={handleReset}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600">Reset to Defaults</button>
          <button onClick={openCreate}
            className={`flex items-center gap-2 px-4 py-2 bg-${accentColor}-600 text-white rounded-lg hover:bg-${accentColor}-700 font-medium text-sm`}>
            <Plus className="w-4 h-4" /> Add New
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        {getData().length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500">No items configured. Add one or reset to defaults.</p>
          </div>
        ) : (
          getData().map(renderItem)
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className={`p-5 border-b bg-${accentColor}-600 text-white flex justify-between items-center rounded-t-2xl`}>
              <h2 className="text-lg font-bold">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {error && <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
              {getForm()}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave}
                className={`px-6 py-2 bg-${accentColor}-600 text-white rounded-lg hover:bg-${accentColor}-700 flex items-center gap-2 font-medium`}>
                <Save className="w-4 h-4" /> {editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-red-800 mb-2">Delete Item</h3>
            <p className="text-gray-600 mb-4">Are you sure you want to remove this configuration item?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigManagement;
